import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AlertController, ToastController } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { NaploService } from '../naplo.service';

/*  MAGÁNSZEMÉLY INGATLAN-BÉRBEADÁSA (/naplo/ingatlan) – az ingatlanok, a bérletek, a havi bevételek
 *  és költségek, és az összesítő: negyedévente mennyi előleget kell befizetni, hova és mikorra, és az
 *  év végén mennyi a különbözet a bevallásban. A cég bérlő (kifizető) maga vonja le az előleget – az
 *  a cég 08-asába kerül (BÉR). Számolás és jogszabályi háttér: backend/naplo-ingatlan.js. */
@Component({
  selector: 'app-naplo-ingatlan',
  templateUrl: './ingatlan.component.html',
  styleUrls: ['../naplo-shared.scss', './ingatlan.component.scss'],
})
export class IngatlanComponent implements OnInit {
  private readonly base = '/api/naplo-ingatlan';
  get aktiv() { return this.naplo.aktiv; }

  ful: 'osszesito' | 'tetelek' | 'ingatlanok' = 'osszesito';
  adat: any = null;
  betolt = false;
  ev = new Date().getFullYear();
  ho = new Date().getMonth() + 1;
  readonly honapok = Array.from({ length: 12 }, (_, i) => i + 1);
  readonly romai = ['I.', 'II.', 'III.', 'IV.'];

  ingSzerk: any = null;
  berletSzerk: any = null;
  tetelSzerk: any = null;

  constructor(private naplo: NaploService, private http: HttpClient, private toast: ToastController, private alert: AlertController) {}

  private get<T = any>(p: string) { return firstValueFrom(this.http.get<T>(this.base + p)); }
  private post<T = any>(p: string, b: any) { return firstValueFrom(this.http.post<T>(this.base + p, b)); }
  private del<T = any>(p: string) { return firstValueFrom(this.http.delete<T>(this.base + p)); }

  async ngOnInit() {
    await this.naplo.init();
    this.ev = this.aktiv.ev || this.ev;
    await this.frissit();
  }
  async frissit() {
    if (!this.aktiv.ugyfelId) return;
    this.betolt = true;
    try { this.adat = await this.get(`/${this.aktiv.ugyfelId}?ev=${this.ev}`); }
    catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült betölteni.'); }
    finally { this.betolt = false; }
  }
  get o(): any { return this.adat?.osszesites || {}; }
  get adoszamos(): boolean { return String(this.adat?.ugyfel?.adoszam || '').replace(/\D/g, '').length >= 8; }
  ingatlanNev(id: string) { return this.adat?.ingatlanok?.find((x: any) => x.id === id)?.megnevezes || ''; }
  berletNev(id: string) { const b = this.adat?.berletek?.find((x: any) => x.id === id); return b ? b.berlo_nev : ''; }
  get aktivBerletek(): any[] { return (this.adat?.berletek || []).filter((b: any) => !b.veg || b.veg >= `${this.ev}-01-01`); }
  get bevetelek(): any[] { return (this.adat?.tetelek || []).filter((t: any) => t.tipus === 'bevetel'); }
  get koltsegek(): any[] { return (this.adat?.tetelek || []).filter((t: any) => t.tipus === 'koltseg'); }

  /* ── ingatlan ── */
  ujIngatlan() { this.ingSzerk = { megnevezes: '', cim: '', hrsz: '', tulajdoni_hanyad: 1, megjegyzes: '', aktiv: true }; }
  szerkIngatlan(i: any) { this.ingSzerk = { ...i, aktiv: !!i.aktiv, tulajdoni_hanyad: Number(i.tulajdoni_hanyad) }; }
  async mentIngatlan() {
    try { await this.post(`/${this.aktiv.ugyfelId}/ingatlan`, this.ingSzerk); this.ingSzerk = null; this.ok('Mentve.'); await this.frissit(); }
    catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült menteni.'); }
  }
  async torolIngatlan(i: any) {
    if (!(await this.megerosit(`${i.megnevezes} törlése?`))) return;
    try { await this.del(`/${this.aktiv.ugyfelId}/ingatlan/${i.id}`); await this.frissit(); }
    catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült törölni.'); }
  }

  /* ── bérlet ── */
  ujBerlet(ingatlanId?: string) {
    this.berletSzerk = { ingatlan_id: ingatlanId || this.adat?.ingatlanok?.[0]?.id || null, berlo_nev: '', berlo_adoszam: '', berlo_kifizeto: true,
      berlo_ugyfel_id: null, havi_dij: null, afa_kulcs: null, koltseg_mod: 'hanyad', koltseg_nyilatkozat: null,
      kezdet: `${this.ev}-01-01`, veg: null, megjegyzes: '' };
  }
  szerkBerlet(b: any) { this.berletSzerk = { ...b, berlo_kifizeto: !!b.berlo_kifizeto, havi_dij: Number(b.havi_dij) }; }
  kifizetoValt(id: string | null) {
    const k = this.adat?.kifizetok?.find((x: any) => x.id === id);
    if (k) { this.berletSzerk.berlo_nev = k.nev; this.berletSzerk.berlo_adoszam = k.adoszam || ''; this.berletSzerk.berlo_kifizeto = true; }
  }
  /** A kifizető által levont havi előleg a bérlet adataiból (előnézet). */
  get berletEloleg(): { koltseg: number; alap: number; eloleg: number } | null {
    const b = this.berletSzerk; if (!b || !b.berlo_kifizeto || !(Number(b.havi_dij) > 0)) return null;
    const bev = Number(b.havi_dij);
    const koltseg = b.koltseg_mod === 'tetel' && b.koltseg_nyilatkozat != null && b.koltseg_nyilatkozat !== ''
      ? Math.min(Math.max(0, Number(b.koltseg_nyilatkozat)), bev * 0.5) : bev * 0.1;
    const alap = Math.max(0, bev - koltseg);
    return { koltseg: Math.round(koltseg), alap: Math.round(alap), eloleg: Math.round(alap * 0.15) };
  }
  async mentBerlet() {
    try { await this.post(`/${this.aktiv.ugyfelId}/berlet`, this.berletSzerk); this.berletSzerk = null; this.ok('Mentve.'); await this.frissit(); }
    catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült menteni.'); }
  }
  async torolBerlet(b: any) {
    if (!(await this.megerosit(`A(z) ${b.berlo_nev} bérletének törlése?`))) return;
    try { await this.del(`/${this.aktiv.ugyfelId}/berlet/${b.id}`); await this.frissit(); }
    catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült törölni.'); }
  }

  /* ── bevétel / költség ── */
  async haviDijak() {
    try {
      const r: any = await this.post(`/${this.aktiv.ugyfelId}/havi`, { ev: this.ev, ho: this.ho });
      this.ok(r.letrehozva ? `${r.letrehozva} bérleti díj felvéve (${this.ev}. ${this.ho}. hó).` : 'Erre a hónapra már minden bérleti díj fel van véve.');
      await this.frissit();
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült.'); }
  }
  ujTetel(tipus: 'bevetel' | 'koltseg') {
    this.tetelSzerk = { tipus, datum: `${this.ev}-${String(this.ho).padStart(2, '0')}-01`, berlet_id: tipus === 'bevetel' ? (this.aktivBerletek[0]?.id || null) : null,
      ingatlan_id: this.adat?.ingatlanok?.[0]?.id || null, megnevezes: '', netto: null, afa: null, megjegyzes: '' };
  }
  async mentTetel() {
    try { await this.post(`/${this.aktiv.ugyfelId}/tetel`, this.tetelSzerk); this.tetelSzerk = null; this.ok('Felvéve.'); await this.frissit(); }
    catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült menteni.'); }
  }
  async torolTetel(t: any) {
    if (!(await this.megerosit('A tétel törlése?'))) return;
    try { await this.del(`/${this.aktiv.ugyfelId}/tetel/${t.id}`); await this.frissit(); }
    catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült törölni.'); }
  }

  private async megerosit(m: string): Promise<boolean> {
    const a = await this.alert.create({ header: 'Megerősítés', message: m, buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Igen', role: 'ok' }] });
    await a.present();
    return (await a.onDidDismiss()).role === 'ok';
  }
  private async ok(m: string) { (await this.toast.create({ message: m, duration: 1800, color: 'success' })).present(); }
  private async hiba(m: string) { (await this.toast.create({ message: m, duration: 3000, color: 'danger' })).present(); }
}
